import { useInvitation } from "@@/backoffice/dashboard/providers/users/invite-user/queries/use-invitation";
import { PageNotFound } from "@@/backoffice/shared/not-found-page";
import { PageLoad } from "@@/pages/page-load";
import { useProvider } from "@@/providers/state/queries/provider/use-provider";
import { AccountIcon } from "@@/shared/account-icon";
import { ButtonPrimaryLight } from "@@/shared/buttons_v2/button-primary-light";
import { VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { GlobalMaxWidthCss } from "@@/shared/global-max-width";
import { HeadTitle } from "@@/shared/head-title";
import { TextBox } from "@@/shared/text";
import { useTheme } from "@emotion/react";
import * as Sentry from "@sentry/react";
import { isInvitationId, joinTranslatables, translation } from "@towni/common";
import { useLocation } from "react-router";
import { useMe } from "../me/me-context";
import { AcceptInvitation } from "./accept-invitation";

const InvitationPage = () => {
    const [me] = useMe();
    const theme = useTheme();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const inviteStr = params.get("invite");
    const isInviteId = isInvitationId(inviteStr);
    const [, inviteQuery] = useInvitation(
        isInviteId && me ? inviteStr : undefined,
    );
    const invite = inviteQuery.data;
    const [, providerQuery] = useProvider(invite?.providerId);
    const provider = providerQuery.data;
    const pending = inviteQuery.isPending || providerQuery.isPending;

    const isEmailVerified = me?.email.verified;

    if (!me) return <InvitationPage_NotLoggedIn />;
    if (!isEmailVerified) return <InvitationPage_EmailNotVerified />;

    if (
        me &&
        me.email.emailAddress.toLowerCase().trim() !==
            invite?.email.toLowerCase().trim()
    ) {
        console.log("InvitationPage_LoggedInWrongUser", {
            me,
            invite,
        });
        return <InvitationPage_LoggedInWrongUser />;
    }

    if (pending)
        return (
            <PageLoad
                text={{
                    sv: "Laddar inbjudan...",
                    en: "Loading invitation...",
                    de: "Lade Einladung...",
                }}
            />
        );

    if (!invite || !provider)
        return (
            <PageNotFound
                reason={translation({
                    en: "Invitation could not be found, it could have expired. Please ask the administrator to send a new one.",
                    sv: "Inbjudan kunde inte hittas, den kanske är för gammal och har gått ut. Be administratören att skicka en ny.",
                    de: "Einladung konnte nicht gefunden werden, sie könnte abgelaufen sein. Bitten Sie den Administrator, eine neue zu senden.",
                })}
            />
        );

    const title = joinTranslatables([
        translation({
            sv: "Kontoinbjudan till ",
            en: "Account invite for ",
            de: "Kontoeinladung für ",
        }),
        provider.meta.title,
    ]);

    return (
        <Sentry.ErrorBoundary>
            <GlobalMaxWidthCss />
            <HeadTitle title={title} />
            <FlexColumn
                mainAxis="center"
                crossAxis="center"
                css={{
                    flex: 1,
                    background: `radial-gradient(circle, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                    // background: `linear-gradient(135deg, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                }}>
                <FlexColumn
                    crossAxis="center"
                    css={{
                        backgroundColor: theme.colors.white.asString,
                        padding: 20,
                        margin: 20,
                        borderRadius: 20,
                        borderStyle: "solid",
                        borderWidth: 1,
                        borderColor: theme.colors.black.light80.asString,
                        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}>
                    <FlexRow mainAxis="space-between" crossAxis="center">
                        <FlexColumn crossAxis="center">
                            <TextBox
                                text={title}
                                size={1.5}
                                weight="900"
                                align="center"
                            />
                        </FlexColumn>
                    </FlexRow>
                    <VerticalDivider L />
                    {!me ? (
                        <AccountIcon showName />
                    ) : (
                        <AcceptInvitation
                            invitation={invite}
                            provider={provider}
                        />
                    )}
                </FlexColumn>
            </FlexColumn>
        </Sentry.ErrorBoundary>
    );
};

const InvitationPage_LoggedInWrongUser = () => {
    const theme = useTheme();
    const [me, { signOut }] = useMe();

    return (
        <Sentry.ErrorBoundary>
            <GlobalMaxWidthCss />
            <HeadTitle
                title={{
                    sv: "Kontoinbjudan",
                    en: "Account invite",
                    de: "Kontoeinladung",
                }}
            />
            <FlexColumn
                mainAxis="center"
                crossAxis="center"
                css={{
                    flex: 1,
                    background: `radial-gradient(circle, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                    // `linear-gradient(135deg, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                }}>
                <FlexColumn
                    crossAxis="center"
                    css={{
                        backgroundColor: theme.colors.white.asString,
                        padding: 40,
                        margin: 20,
                        borderRadius: 20,
                        borderStyle: "solid",
                        borderWidth: 1,
                        borderColor: theme.colors.black.light80.asString,
                        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}>
                    <FlexRow mainAxis="space-between" crossAxis="center">
                        <FlexColumn crossAxis="center">
                            <TextBox
                                text={{
                                    sv: "Kontoinbjudan",
                                    en: "Account invite",
                                    de: "Kontoeinladung",
                                }}
                                size={1.5}
                                weight="900"
                            />
                        </FlexColumn>
                    </FlexRow>
                    <VerticalDivider L />
                    <TextBox
                        text={{
                            sv: "Du har blivit inbjuden till att ta del av ett företagskonto på towni. Du är däremot inloggad som en annan användare än den som inbjudan skickades till. För att gå vidare, logga ut och logga sedan in eller skapa ett konto för den e-postadress inbjudan skickades till.",
                            en: "You have been invited to join a company account on towni/destio. However, you are logged in as a different user than the one the invitation was sent to. To proceed, log out and then log in or create an account for the email address the invitation was sent to.",
                            de: "Sie wurden eingeladen, einem Firmenkonto auf towni/destio beizutreten. Sie sind jedoch als anderer Benutzer angemeldet als der, an den die Einladung gesendet wurde. Um fortzufahren, melden Sie sich ab und dann an oder erstellen Sie ein Konto für die E-Mail-Adresse, an die die Einladung gesendet wurde.",
                        }}
                        userSelect="text"
                        css={{ maxWidth: 400, textAlign: "center" }}
                    />
                    <VerticalDivider ML />
                    <TextBox
                        text={{
                            sv: `Alternativt kan du be administratören att bjuda in dig igen men denna gång istället bjuda in den e-postadress du redan använder som är verifierad för ditt konto: ${me?.email.emailAddress}`,
                            en: `Alternatively, you can ask the administrator to invite you again but this time instead invite the email address you already use that is verified for your account: ${me?.email.emailAddress}`,
                            de: `Alternativ können Sie den Administrator bitten, Sie erneut einzuladen, aber diesmal stattdessen die E-Mail-Adresse einzuladen, die Sie bereits verwenden und die für Ihr Konto verifiziert ist: ${me?.email.emailAddress}`,
                        }}
                        userSelect="text"
                        css={{ maxWidth: 400, textAlign: "center" }}
                    />
                    <VerticalDivider ML />
                    <ButtonPrimaryLight
                        css={{
                            paddingLeft: 25,
                            paddingRight: 25,
                            borderRadius: 3000,
                        }}
                        onClick={async () => {
                            await signOut();
                        }}>
                        <TextBox
                            text={translation({
                                sv: "Logga ut",
                                en: "Log out",
                                de: "Ausloggen",
                            })}
                        />
                    </ButtonPrimaryLight>
                    <VerticalDivider XS />
                </FlexColumn>
            </FlexColumn>
        </Sentry.ErrorBoundary>
    );
};
const InvitationPage_NotLoggedIn = () => {
    const theme = useTheme();
    return (
        <Sentry.ErrorBoundary>
            <GlobalMaxWidthCss />
            <HeadTitle
                title={{
                    sv: "Kontoinbjudan",
                    en: "Account invite",
                }}
            />
            <FlexColumn
                mainAxis="center"
                crossAxis="center"
                css={{
                    flex: 1,
                    background: `radial-gradient(circle, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                    // background: `linear-gradient(135deg, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                }}>
                <FlexColumn
                    crossAxis="center"
                    css={{
                        backgroundColor: theme.colors.white.asString,
                        padding: 20,
                        margin: 20,
                        borderRadius: 20,
                        borderStyle: "solid",
                        borderWidth: 1,
                        borderColor: theme.colors.black.light80.asString,
                        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}>
                    <FlexRow mainAxis="space-between" crossAxis="center">
                        <FlexColumn crossAxis="center">
                            <TextBox
                                text={{
                                    sv: "Kontoinbjudan",
                                    en: "Account invite",
                                    de: "Kontoeinladung",
                                }}
                                size={1.5}
                                weight="900"
                            />
                        </FlexColumn>
                    </FlexRow>
                    <VerticalDivider L />
                    <TextBox
                        text={{
                            sv: "Du har blivit inbjuden till att ta del av ett företagskonto på towni/destio. För att acceptera inbjudan, vänligen logga antingen in till ett konto eller skapa ett konto där den e-postadress som är inbjuden används och är verifierad.",
                            en: "You have been invited to join a company account on towni/destio. To accept the invitation, please either log in to an account or create an account where the email address that is invited is used and verified.",
                            de: "Sie wurden eingeladen, einem Firmenkonto auf towni/destio beizutreten. Um die Einladung anzunehmen, melden Sie sich bitte entweder bei einem Konto an oder erstellen Sie ein Konto, bei dem die eingeladene E-Mail-Adresse verwendet und verifiziert wird.",
                        }}
                        css={{ maxWidth: 500, textAlign: "center" }}
                    />
                    <VerticalDivider L />
                    <AccountIcon
                        showName
                        size={1.125}
                        loginButtonText={translation({
                            sv: "Skapa konto / Logga in",
                            en: "Create account / Log in",
                            de: "Konto erstellen / Einloggen",
                        })}
                    />
                    <VerticalDivider XS />
                </FlexColumn>
            </FlexColumn>
        </Sentry.ErrorBoundary>
    );
};

const InvitationPage_EmailNotVerified = () => {
    const theme = useTheme();
    return (
        <Sentry.ErrorBoundary>
            <GlobalMaxWidthCss />
            <HeadTitle
                title={{
                    sv: "Kontoinbjudan",
                    en: "Account invite",
                }}
            />
            <FlexColumn
                mainAxis="center"
                crossAxis="center"
                css={{
                    flex: 1,
                    background: `radial-gradient(circle, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                    // background: `linear-gradient(135deg, ${theme.colors.primary.asString}, ${theme.colors.primary.dark.asString})`,
                }}>
                <FlexColumn
                    crossAxis="center"
                    css={{
                        backgroundColor: theme.colors.white.asString,
                        padding: 20,
                        margin: 20,
                        borderRadius: 20,
                        borderStyle: "solid",
                        borderWidth: 1,
                        borderColor: theme.colors.black.light80.asString,
                        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}>
                    <FlexRow mainAxis="space-between" crossAxis="center">
                        <FlexColumn crossAxis="center">
                            <TextBox
                                text={{
                                    sv: "Kontoinbjudan",
                                    en: "Account invite",
                                    de: "Kontoeinladung",
                                }}
                                size={1.5}
                                weight="900"
                            />
                        </FlexColumn>
                    </FlexRow>
                    <VerticalDivider L />
                    <TextBox
                        text={{
                            sv: "Din e-postadress är inte verifierad. Vänligen verifiera din e-postadress innan du accepterar inbjudan.",
                            en: "Your email is not verified. Please verify your email before accepting the invitation.",
                        }}
                        css={{ maxWidth: 500, textAlign: "center" }}
                    />
                </FlexColumn>
            </FlexColumn>
        </Sentry.ErrorBoundary>
    );
};

export { InvitationPage };
